import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { gameState } from '../state/gameState.js';
import { showToast } from './Toast.js';
import { showModal } from './Modal.js';
import { messageFor } from '../api/errors.js';
import { bikeCard } from './bikeCard.mjs';
import { bikeSession } from './bikeSession.js';

// A bike's card in the walk-through shop (bike plan 5), in CarDetailsModal's furniture: the design's
// name, the bike and its level, „Masz już ten rowerek” when you own that design, a Szybkość bar, the price,
// then Kup or Sprzedaj, and Zamknij. bikeCard.mjs decides every word.
//
//   await showBikeCardModal(scene, 'pink', 'arbuz');

const W = 560;
const ROW_H = 36;
const BTN_H = 50, BTN_GAP = 14;
const TABLE_W = W - 72;
const BAR_W = 200, BAR_H = 14;
const BOTTOM_PAD = 24;
const ROW_TINT = [0xffffff, 0xf1ece2];

function viewCentre(scene) {
  const cam = scene.cameras.main;
  return { ox: cam.scrollX, oy: cam.scrollY, x: cam.scrollX + GAME_WIDTH / 2, y: cam.scrollY + GAME_HEIGHT / 2 };
}

/** Plan 4's confirm step before a bike goes back to the shop; resolves true when it was sold. */
export async function confirmSellBike(scene, o) {
  const ok = await showModal(scene, {
    title: 'Na pewno?',
    body: `${o.name} (${o.designName})\nSklep odkupi go za ${o.sellPrice} {coin}. Smar w łańcuchu przepadnie.`,
    confirmLabel: 'Sprzedaj',
  });
  if (!ok) return false;
  try {
    await gameState.sellBike(o.design);
    showToast(scene, `Sprzedane za ${o.sellPrice} {coin}`);
    return true;
  } catch (e) { showToast(scene, messageFor(e)); return false; }
}

async function buy(scene, o) {
  try {
    await gameState.buyBike(o.design);
    showToast(scene, `Kupiono: ${o.name} (${o.designName}) {bike} Czeka przed sklepem`);
  } catch (e) { showToast(scene, messageFor(e)); }
}

/** Resolves once the card is closed (after its action, if one was taken). */
export function showBikeCardModal(scene, type, design) {
  const card = bikeCard(gameState.raw, type, design, bikeSession.riding ? bikeSession.ridingId : null);
  return new Promise((resolve) => {
    const rows = [
      { icon: '{rocket}', label: 'Szybkość', bar: card.speedShare },
      { icon: '{coin}', label: 'Cena', value: card.price == null ? '?' : `${card.price} {coin}` },
    ];
    const subTop = 62, noteTop = 92;
    const tableTop = card.note ? 126 : 100;
    const tableH = rows.length * ROW_H;
    const nBtn = card.actions.length + 1;
    const H = tableTop + tableH + 18 + nBtn * (BTN_H + BTN_GAP) + BOTTOM_PAD;
    const { ox, oy, x, y } = viewCentre(scene);
    const top = y - H / 2;
    const layer = scene.add.container(0, 0).setDepth(2000);
    lockInput(scene);

    const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
      .setOrigin(0, 0).setInteractive();
    const box = scene.add.rectangle(x, y, W, H, 0xffffff, 1).setStrokeStyle(4, 0x333333);
    const title = iconText(scene, x, top + 20, card.title, {
      fontFamily: 'system-ui', fontSize: '26px', color: '#000', fontStyle: 'bold',
      align: 'center', wordWrap: { width: W - 90 },
    }).setOrigin(0.5, 0);
    const sub = iconText(scene, x, top + subTop, card.subtitle, {
      fontFamily: 'system-ui', fontSize: '18px', color: '#555', align: 'center',
    }).setOrigin(0.5, 0);
    layer.add([overlay, box, title, sub]);
    if (card.note) {
      layer.add(iconText(scene, x, top + noteTop, card.note, {
        fontFamily: 'system-ui', fontSize: '18px', color: '#7a3d00', fontStyle: 'bold', align: 'center',
      }).setOrigin(0.5, 0));
    }
    enlargeForTouch(scene, layer, { x, y, w: W, h: H }, { overlay });

    const left = x - TABLE_W / 2, right = x + TABLE_W / 2;
    rows.forEach((row, i) => {
      const cy = top + tableTop + i * ROW_H + ROW_H / 2;
      layer.add(scene.add.rectangle(x, cy, TABLE_W, ROW_H, ROW_TINT[i % 2], 1));
      layer.add(iconText(scene, left + 12, cy, row.icon, { fontFamily: 'system-ui', fontSize: '19px' }).setOrigin(0, 0.5));
      layer.add(iconText(scene, left + 44, cy, row.label, {
        fontFamily: 'system-ui', fontSize: '18px', color: '#333',
      }).setOrigin(0, 0.5));
      if (row.bar != null) {
        // The greased top speed against the fastest bike: the same bar for every design of a bike.
        const bx = right - 12 - BAR_W;
        layer.add(scene.add.rectangle(bx, cy, BAR_W, BAR_H, 0xe2dccf).setOrigin(0, 0.5).setStrokeStyle(2, 0x888070));
        layer.add(scene.add.rectangle(bx, cy, BAR_W * Math.min(1, row.bar), BAR_H, 0x5aa864).setOrigin(0, 0.5));
      } else {
        layer.add(iconText(scene, right - 12, cy, row.value, {
          fontFamily: 'system-ui', fontSize: '18px', color: '#000', fontStyle: 'bold',
        }).setOrigin(1, 0.5));
      }
    });
    layer.add(scene.add.rectangle(x, top + tableTop + tableH / 2, TABLE_W, tableH).setStrokeStyle(2, 0xbbb2a2));

    const shut = () => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); };
    const close = (result) => { shut(); resolve(result); };
    const closeBtn = scene.add.rectangle(x + W / 2 - 24, top + 24, 36, 36, 0xaa3333, 1)
      .setStrokeStyle(2, 0x000000).setInteractive({ useHandCursor: true });
    const closeTxt = iconText(scene, x + W / 2 - 24, top + 24, '×', {
      fontFamily: 'system-ui', fontSize: '20px', color: '#fff', fontStyle: 'bold',
    }).setOrigin(0.5);
    layer.add([closeBtn, closeTxt]);
    closeBtn.on('pointerup', () => close(null));
    overlay.on('pointerup', () => close(null));

    const run = {
      buy: () => buy(scene, card.offer),
      sell: () => confirmSellBike(scene, card.offer),
    };
    const all = [...card.actions, { label: 'Zamknij', quiet: true }];
    const firstY = top + tableTop + tableH + 18 + BTN_H / 2;
    all.forEach((act, i) => {
      const by = firstY + i * (BTN_H + BTN_GAP);
      const fill = act.disabled ? 0xcccccc : act.quiet ? 0xe8e8e8 : 0xffd166;
      const btn = scene.add.rectangle(x, by, W - 90, BTN_H, fill).setStrokeStyle(2, 0x333333);
      const txt = iconText(scene, x, by, act.label, {
        fontFamily: 'system-ui', fontSize: '20px', color: act.disabled ? '#888' : '#000',
        fontStyle: 'bold', align: 'center', wordWrap: { width: W - 120 },
      }).setOrigin(0.5);
      layer.add([btn, txt]);
      if (act.disabled) return;
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerup', async () => {
        btn.disableInteractive();
        // The card closes first (the sell confirm is a modal of its own), then the action runs.
        shut();
        resolve(act.kind ? await run[act.kind]() : null);
      });
    });
  });
}
